import { io, Socket } from 'socket.io-client';
import { ref } from 'vue';
import { SOCKET_EVENTS } from '@shared/socket-events';
import { useAuthStore } from '@/stores/auth';
import { useInboxStore } from '@/stores/inbox';

let socket: Socket | null = null;

const connectionState = ref<'idle' | 'connecting' | 'connected' | 'disconnected'>('idle');
const lastEventAt = ref<number | null>(null);

export function getSocket(): Socket | null {
  return socket;
}

export function useSocket() {
  function connect() {
    if (socket && socket.connected) return;
    const auth = useAuthStore();
    // Only connect when authenticated — server middleware rejects without cookie
    if (!auth.user) return;
    connectionState.value = 'connecting';
    socket = io({
      path: '/socket.io',
      withCredentials: true,
      // Prefer websocket, fall back to long-polling. Polling transport is
      // most reliable behind dev proxies and works even when WS upgrade fails.
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      console.log('[socket] connected', socket?.id);
      connectionState.value = 'connected';
      wireListeners();
    });
    socket.on('disconnect', (reason) => {
      console.log('[socket] disconnected:', reason);
      connectionState.value = 'disconnected';
    });
    socket.on('connect_error', (err) => {
      console.warn('[socket] connect_error:', err.message);
    });
    socket.on('reconnect', (attempt) => {
      console.log('[socket] reconnected after', attempt, 'attempts');
    });
  }

  function wireListeners() {
    if (!socket) return;
    const inbox = useInboxStore();

    socket.on(SOCKET_EVENTS.MAIL_NEW, (payload: any) => {
      console.log('[socket] MAIL_NEW', payload?.email_id);
      lastEventAt.value = Date.now();
      inbox.handleSocketNew(payload);
    });
    socket.on(SOCKET_EVENTS.MAIL_DELETED, (payload: any) => {
      lastEventAt.value = Date.now();
      inbox.handleSocketDeleted(payload);
    });
    socket.on(SOCKET_EVENTS.MAIL_RESTORED, (payload: any) => {
      lastEventAt.value = Date.now();
      inbox.handleSocketRestored(payload);
    });
    socket.on(SOCKET_EVENTS.MAIL_PURGED, (payload: any) => {
      inbox.handleSocketDeleted(payload);
    });
    socket.on(SOCKET_EVENTS.MAIL_READ, (payload: any) => {
      inbox.handleSocketRead(payload);
    });
    socket.on(SOCKET_EVENTS.CLIENT_STATUS, (payload: any) => {
      const auth = useAuthStore();
      auth.handleSocketStatus(payload);
    });
  }

  function disconnect() {
    socket?.disconnect();
    socket = null;
    connectionState.value = 'idle';
  }

  return { connect, disconnect, socket, connectionState, lastEventAt };
}